import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { leagueApi, DashboardLeague } from '../services/api-client';
import { MobileLayout } from '../components/layout/MobileLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PullToRefresh } from '../components/layout/PullToRefresh';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { formatDate } from '../utils/dateTime';

interface LeagueEntry {
  id: string;
  name: string;
  tournamentName: string;
  tournamentId: string | null;
  tournamentStatus: string;
  tournamentFinalized: boolean;
  startDate: string | null;
  endDate: string | null;
  memberCount: number;
  isMember: boolean;
  myRank: number | null;
  myPoints: number;
  teamName: string | null;
  rosterLocked: boolean;
}

export default function LeaguesPage() {
  const { isAdmin } = useAuthContext();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<LeagueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [adRefreshKey, setAdRefreshKey] = useState(0);

  useEffect(() => {
    loadLeagues();
  }, []);

  const loadLeagues = async () => {
    try {
      setLoading(true);

      // Use consolidated endpoint - single API call instead of 11+
      const { leagues: dashboardLeagues } = await leagueApi.myDashboard();

      // Transform dashboard leagues to league entries
      const leagueEntries: LeagueEntry[] = dashboardLeagues.map((league: DashboardLeague) => {
        // Get the first team (most users have only 1 team per league)
        // If user has multiple teams, we'll show the first one's rank/points
        const firstTeam = league.userTeams[0];

        return {
          id: league.id,
          name: league.name,
          tournamentName: league.tournamentName || 'Unknown Tournament',
          tournamentId: league.tournamentId,
          tournamentStatus: league.tournamentStatus || 'upcoming',
          tournamentFinalized: league.tournamentFinalized ?? false,
          startDate: league.tournamentStartDate,
          endDate: league.tournamentEndDate,
          memberCount: league.memberCount,
          isMember: league.userTeams.length > 0,
          myRank: firstTeam?.rank || null,
          myPoints: firstTeam?.totalPoints ?? 0,
          teamName: firstTeam?.teamName || null,
          rosterLocked: league.rosterLocked,
        };
      });

      setLeagues(leagueEntries);
    } catch (error) {
      console.error('Failed to load leagues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadLeagues();
    setAdRefreshKey((prev) => prev + 1);
  };

  const handleJoinLeague = () => {
    if (!inviteCode.trim()) {
      alert('Please enter an invite code');
      return;
    }

    const code = inviteCode.trim().toUpperCase();
    setInviteCode('');
    setShowJoinModal(false);
    navigate(`/join/${code}`, { state: { allowRejoin: true } });
  };

  const isAutoArchived = (league: LeagueEntry) => {
    if (!league.endDate) return false;
    const archiveDate = new Date(league.endDate);
    archiveDate.setDate(archiveDate.getDate() + 3);
    return new Date() > archiveDate;
  };

  const groupedLeagues = {
    active: leagues.filter((l) => l.isMember && !isAutoArchived(l)),
    archived: leagues.filter((l) => l.isMember && isAutoArchived(l)),
  };

  const renderLeagueCard = (league: LeagueEntry) => {
    return (
      <div
        key={league.id}
        onClick={() => navigate(`/leagues/${league.id}`)}
        className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow active:scale-[0.98] active:shadow-sm"
      >
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 truncate">{league.name}</h3>
            <div className="flex items-center gap-1.5">
              <p className="text-sm text-gray-600 truncate">{league.tournamentName}</p>
              {league.tournamentFinalized && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 flex-shrink-0">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Final
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0 ml-2">
            <span>
              {league.memberCount} {league.memberCount === 1 ? 'team' : 'teams'}
            </span>
            {league.startDate && (
              <>
                <span className="text-gray-300">·</span>
                <span>{formatDate(league.startDate)}</span>
              </>
            )}
          </div>
        </div>

        {league.isMember && league.myRank !== null && (
          <div className="text-sm mt-1">
            <span className="font-semibold text-gray-900">
              #{league.myRank} •{' '}
              {league.rosterLocked ? (league.myPoints !== undefined ? league.myPoints : 0) : 0} pts
            </span>
          </div>
        )}

        {!league.isMember && isAdmin && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <span className="text-xs text-gray-500">Admin view - not a member</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <MobileLayout>
      <PageHeader
        title="Leagues"
        actions={
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 text-gray-300 hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50"
            aria-label="Refresh"
          >
            <svg
              className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        }
      />

      <PullToRefresh onRefresh={handleRefresh}>
        <div className="px-4 py-4 space-y-6">
          {loading && leagues.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading leagues...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Active Leagues - No heading */}
              {groupedLeagues.active.length > 0 && (
                <div className="space-y-3 mb-6">{groupedLeagues.active.map(renderLeagueCard)}</div>
              )}

              {/* Ad Banner - After active leagues */}
              <div className="py-2">
                <AdBanner slot={AD_SLOTS.leagues} format="horizontal" refreshKey={adRefreshKey} />
              </div>

              {/* Archived Leagues - Collapsible */}
              {groupedLeagues.archived.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowArchived(!showArchived)}
                    className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 hover:text-gray-900"
                  >
                    <span>Archived ({groupedLeagues.archived.length})</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${showArchived ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {showArchived && (
                    <div className="space-y-3">{groupedLeagues.archived.map(renderLeagueCard)}</div>
                  )}
                </div>
              )}

              {leagues.length === 0 && (
                <div className="space-y-4">
                  {/* Join a League Card */}
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Join a League</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Have an invite code? Enter it below to join.
                    </p>
                    <input
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === 'Enter' && handleJoinLeague()}
                      placeholder="INVITE CODE"
                      maxLength={10}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center font-mono text-base mb-3"
                    />
                    <button
                      onClick={handleJoinLeague}
                      disabled={!inviteCode.trim()}
                      className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                    >
                      Join League
                    </button>
                  </div>

                  {/* Create a League Card */}
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Create a League</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Start a new league and invite your friends.
                    </p>
                    <button
                      onClick={() => navigate('/leagues/create')}
                      className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                    >
                      Create League
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </PullToRefresh>

      {/* FAB dismiss backdrop - only when menu is open */}
      {showFabMenu && <div className="fixed inset-0 z-30" onClick={() => setShowFabMenu(false)} />}

      {/* Floating Action Button - constrained to phone column */}
      <div className="fixed bottom-20 left-0 right-0 max-w-md mx-auto z-40 pointer-events-none">
        <div className="flex justify-end pr-4">
          <button
            onClick={() => setShowFabMenu(!showFabMenu)}
            className="w-14 h-14 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95 flex items-center justify-center pointer-events-auto"
            aria-label="Quick actions"
          >
            <svg
              className={`w-6 h-6 transition-transform ${showFabMenu ? 'rotate-45' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* FAB Menu Popup - constrained to phone column */}
      {showFabMenu && (
        <div className="fixed bottom-36 left-0 right-0 max-w-md mx-auto z-40 pointer-events-none">
          <div className="flex justify-end pr-4">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden pointer-events-auto">
              <button
                onClick={() => {
                  setShowFabMenu(false);
                  navigate('/leagues/create');
                }}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors w-full text-left border-b border-gray-100"
              >
                <span className="text-xl">➕</span>
                <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                  Create League
                </span>
              </button>
              <button
                onClick={() => {
                  setShowFabMenu(false);
                  setShowJoinModal(true);
                }}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors w-full text-left border-b border-gray-100"
              >
                <span className="text-xl">🔗</span>
                <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                  Join League
                </span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => {
                    setShowFabMenu(false);
                    navigate('/tournaments');
                  }}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors w-full text-left"
                >
                  <span className="text-xl">🏆</span>
                  <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                    Tournaments
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Join League Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 max-w-md mx-auto bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Join a League</h2>
            <p className="text-sm text-gray-600 mb-4">
              Enter the invite code you received to join a league.
            </p>
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleJoinLeague()}
              placeholder="INVITE CODE"
              maxLength={10}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center font-mono text-base mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handleJoinLeague}
                disabled={!inviteCode.trim()}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
              <button
                onClick={() => {
                  setShowJoinModal(false);
                  setInviteCode('');
                }}
                className="flex-1 px-4 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileLayout>
  );
}
